import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, Card, Alert, Loader, Badge, Dialog } from '@dreamtree-org/twreact-ui';
import { ChevronLeft, ChevronRight, RefreshCw, Pencil, Trash2, Plus, ArrowRight, Database } from 'lucide-react';
import { useAppApi } from '@/lib/api';

const STATUS_VARIANT = {
  missing: 'warning',
  changed: 'primary',
  same: 'default',
  extra: 'error',
};

const PAGE_SIZE = 50;

const errText = (ex, fallback) => (ex.response && ex.response.data && ex.response.data.error) || ex.message || fallback;

/** Cell display — long values are clipped so one wide column can't blow out the row. */
const fmt = (v) => {
  if (v === null || v === undefined) return '—';
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
};

/**
 * Edit-form value. Unlike fmt() this must NOT truncate — whatever it produces is what
 * gets written back to the target if the field is left untouched.
 */
const toFieldValue = (v) => {
  if (v == null) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
};

/**
 * Utility Tools › Data Migration › per-table row diff + reconcile.
 *
 * Reached by clicking a table on a run. Both endpoints are that run's external databases;
 * the server decrypts them from the run row, so nothing sensitive travels through here.
 * Sync copies source→target; Edit/Delete/Add write to the TARGET only.
 */
export default function DataMigrationTableDiff() {
  const { runId, table } = useParams();
  const navigate = useNavigate();
  const { fetchApi } = useAppApi();

  const [which, setWhich] = useState('source');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  const [editRow, setEditRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addValues, setAddValues] = useState({});
  const [confirmRow, setConfirmRow] = useState(null);

  const columns = data?.columns || [];
  const pk = data?.primaryKey || ['id'];
  const counts = data?.counts || {};
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const pkKey = (row) => pk.map((c) => row.pk[c]).join('-');
  const pkLabel = (row) => pk.map((c) => `${c}=${fmt(row.pk[c])}`).join(' · ');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi('dataMigrationDiff', { runId, table, page, limit: PAGE_SIZE, which });
      setData(res);
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Failed to load the diff'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fetchApi, runId, table, page, which]);

  useEffect(() => { load(); }, [load]);

  const syncRow = async (row) => {
    setBusy(pkKey(row));
    setErr('');
    try {
      await fetchApi('dataMigrationRowSync', { runId, table, pk: pkKey(row), where: row.pk });
      setNotice(`${pkLabel(row)} copied to target.`);
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not sync the row'));
    } finally {
      setBusy(null);
    }
  };

  const deleteRow = async () => {
    const row = confirmRow;
    if (!row) return;
    setConfirmRow(null);
    setBusy(pkKey(row));
    setErr('');
    try {
      await fetchApi('dataMigrationRowDelete', { runId, table, pk: pkKey(row), where: row.pk });
      setNotice(`${pkLabel(row)} removed from target.`);
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not delete the row'));
    } finally {
      setBusy(null);
    }
  };

  const openEdit = (row) => {
    const base = row.target || row.source || {};
    const values = {};
    columns.forEach((c) => { values[c] = toFieldValue(base[c]); });
    setEditRow({ row, values });
  };

  const saveEdit = async () => {
    const { row, values } = editRow;
    setBusy(pkKey(row));
    setErr('');
    try {
      await fetchApi('dataMigrationRowUpsert', { runId, table, pk: pkKey(row), where: row.pk, data: values });
      setNotice(`${pkLabel(row)} updated on target.`);
      setEditRow(null);
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not update the row'));
    } finally {
      setBusy(null);
    }
  };

  const saveAdd = async () => {
    setErr('');
    try {
      await fetchApi('dataMigrationRowUpsert', { runId, table, data: addValues });
      setNotice('Row inserted into target.');
      setAddOpen(false);
      setAddValues({});
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not insert the row'));
    }
  };

  const nonPkColumns = columns.filter((c) => !pk.includes(c));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="secondary" size="sm" onClick={() => navigate('/tools/data-migration')} aria-label="Back">
            <ChevronLeft size={18} />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Database size={16} className="text-primary-600" />
              <h1 className="text-xl font-bold font-mono truncate">{table}</h1>
            </div>
            <p className="text-xs opacity-60 font-mono truncate">
              {data?.sourceLabel || 'source'} <ArrowRight className="inline" size={11} /> {data?.targetLabel || 'target'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {which === 'source' ? (
            <>
              <Badge variant="warning">Missing {counts.missing ?? 0}</Badge>
              <Badge variant="primary">Changed {counts.changed ?? 0}</Badge>
              <Badge variant="default">In sync {counts.same ?? 0}</Badge>
            </>
          ) : (
            <Badge variant="error">Extra {counts.extra ?? 0}</Badge>
          )}
          <span className="text-[11px] opacity-50">src {data?.sourceTotal ?? '—'} · tgt {data?.targetTotal ?? '—'}</span>
          <Button size="sm" leftIcon={<Plus size={15} />} onClick={() => { setAddValues({}); setAddOpen(true); }}>Add row</Button>
        </div>
      </div>

      {/* Two different questions: which source rows are out of sync, and which target rows
          have no source counterpart at all. They page independently. */}
      <div className="flex gap-1 p-1 rounded-xl border border-secondary-200 dark:border-secondary-700 w-fit text-sm">
        {[['source', 'Source rows (diff)'], ['target-extra', 'Target-only (extra)']].map(([val, lbl]) => (
          <button
            key={val}
            type="button"
            onClick={() => { setWhich(val); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg font-medium transition ${which === val ? 'bg-primary-50 text-primary-700' : 'opacity-60 hover:opacity-100'}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {err ? <Alert variant="error" title="Table diff">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="Table diff">{notice}</Alert> : null}

      <Card className="overflow-x-auto">
        {loading && <div className="p-10"><Loader text="Loading diff…" /></div>}
        {!loading && !data?.rows?.length && (
          <div className="p-10 text-center text-sm opacity-50">No rows to show on this page.</div>
        )}
        {!loading && !!data?.rows?.length && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider opacity-50 border-b border-secondary-200 dark:border-secondary-700">
                <th className="py-2 px-3">Key / status</th>
                {nonPkColumns.map((c) => <th key={c} className="py-2 px-3 font-mono normal-case">{c}</th>)}
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => {
                const k = pkKey(row);
                const view = row.target || row.source || {};
                const isBusy = busy === k;
                return (
                  <tr key={k} className="border-b border-secondary-100 dark:border-secondary-800 last:border-0 align-top">
                    <td className="py-2 px-3 whitespace-nowrap">
                      <div className="font-mono text-xs">{pkLabel(row)}</div>
                      <Badge variant={STATUS_VARIANT[row.status] || 'default'}>{row.status}</Badge>
                    </td>
                    {nonPkColumns.map((c) => {
                      const changed = row.status === 'changed' && row.changedColumns?.includes(c);
                      return (
                        <td key={c} className="py-2 px-3 max-w-[16rem]">
                          {changed ? (
                            // Target value struck through above the source value that would replace it.
                            <div className="space-y-0.5">
                              <div className="text-error-600 line-through truncate" title={fmt(row.target?.[c])}>{fmt(row.target?.[c])}</div>
                              <div className="text-success-700 font-medium truncate" title={fmt(row.source?.[c])}>{fmt(row.source?.[c])}</div>
                            </div>
                          ) : (
                            <span className="opacity-70 truncate block" title={fmt(view[c])}>{fmt(view[c])}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        {row.status === 'missing' || row.status === 'changed' ? (
                          <Button variant="secondary" size="sm" onClick={() => syncRow(row)} disabled={isBusy} title="Sync source → target">
                            <RefreshCw size={15} />
                          </Button>
                        ) : null}
                        {/* A "missing" row does not exist on the target, so there is nothing
                            there to edit or delete. */}
                        {row.status !== 'missing' ? (
                          <>
                            <Button variant="secondary" size="sm" onClick={() => openEdit(row)} disabled={isBusy} title="Edit target row">
                              <Pencil size={15} />
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => setConfirmRow(row)} disabled={isBusy} title="Delete target row">
                              <Trash2 size={15} />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {!loading && data ? (
        <div className="flex items-center justify-between text-sm opacity-70">
          <span>Page {page} of {totalPages} · {total} {which === 'source' ? 'source' : 'target'} rows</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} leftIcon={<ChevronLeft size={15} />}>Prev</Button>
            <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} rightIcon={<ChevronRight size={15} />}>Next</Button>
          </div>
        </div>
      ) : null}

      {editRow ? (
        <Dialog isOpen onClose={() => setEditRow(null)} title={`Edit target row — ${pkLabel(editRow.row)}`} size="lg">
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            {columns.map((c) => (
              <Input
                key={c}
                label={pk.includes(c) ? `${c} (pk)` : c}
                value={editRow.values[c] ?? ''}
                disabled={pk.includes(c)}
                onChange={(e) => setEditRow((s) => ({ ...s, values: { ...s.values, [c]: e.target.value } }))}
              />
            ))}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setEditRow(null)}>Cancel</Button>
              <Button onClick={saveEdit} loading={busy === pkKey(editRow.row)}>Save to target</Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {addOpen ? (
        <Dialog isOpen onClose={() => setAddOpen(false)} title={`Add row to target — ${table}`} size="lg">
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            {columns.map((c) => (
              <Input
                key={c}
                label={c}
                value={addValues[c] ?? ''}
                onChange={(e) => setAddValues((s) => ({ ...s, [c]: e.target.value }))}
              />
            ))}
            <p className="text-[11px] opacity-60">
              Leave a column blank to omit it, so defaults and auto-increment apply. FK columns must
              reference rows that already exist in the target.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={saveAdd}>Insert</Button>
            </div>
          </div>
        </Dialog>
      ) : null}

      {confirmRow ? (
        <Dialog isOpen onClose={() => setConfirmRow(null)} title="Delete target row">
          <div className="space-y-4">
            <p className="text-sm">Delete {pkLabel(confirmRow)} from the target? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmRow(null)}>Cancel</Button>
              <Button variant="destructive" onClick={deleteRow}>Delete</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
